/**
 * scanDetector — tells a keyboard-wedge barcode scan apart from a human
 * typing, WITHOUT ever taking keys away from the input.
 *
 * Owner 2026-09-04: the terminal's old detector flipped into "scanning" after
 * two keys < 50 ms apart and then swallowed every following key. Fast typing
 * ("Solomon") tripped it constantly: letters vanished, or "Barcode 'Solo'
 * not found" popped. 50 ms is inside normal typing speed, and the detector
 * destroyed input before it had evidence.
 *
 * This detector only reports a scan when the keystrokes carry a scanner's
 * fingerprint:
 *   • WITH a suffix  — ≥ minChars printable keys, EVERY gap < charGapMs,
 *                      then Enter arriving < charGapMs after the last key.
 *   • WITHOUT suffix — ≥ noSuffixMinChars keys, every gap < charGapMs, then
 *                      silence (the caller flushes after settleMs).
 * Anything else is typing. A human burst can hit 40–60 ms between two keys,
 * but not < charGapMs across 4+ keys AND an Enter 40 ms behind the last one.
 *
 * Keys are never blocked here. The caller lets them reach the input and,
 * on a confirmed scan, clears the input and processes the barcode.
 *
 * Pure: feed(key, now) and flush(now) — no timers, no DOM. Unit-tested with
 * real timings in scanDetector.test.js.
 */

export const SCAN_DEFAULTS = Object.freeze({
  charGapMs: 40,        // scanner keys arrive 8–20 ms apart (Newland H10P); BT wedges ≤ 35
  minChars: 4,          // shortest barcode we accept when an Enter suffix confirms it
  noSuffixMinChars: 8,  // without a suffix we need a longer machine-speed burst
  settleMs: 150,        // silence after the last key before a no-suffix burst is judged
  humanResetMs: 300,    // a pause this long means a new word/scan starts
});

export function createScanDetector(opts = {}) {
  const cfg = { ...SCAN_DEFAULTS, ...opts };
  let buf = '';
  let firstMs = 0;
  let lastMs = 0;
  let maxGap = 0;

  const reset = () => { buf = ''; firstMs = 0; lastMs = 0; maxGap = 0; };
  const machineSpeed = () => maxGap < cfg.charGapMs;

  /**
   * Feed one keydown. `key` is the DOM KeyboardEvent.key.
   * Returns { scan, pending }:
   *   scan    — the confirmed barcode (Enter-suffixed), or null
   *   pending — true when a burst is open and the caller should arm a
   *             settle timer that calls flush()
   */
  function feed(key, now) {
    if (key === 'Enter') {
      const gapToEnter = lastMs ? now - lastMs : Infinity;
      const isScan = buf.length >= cfg.minChars && machineSpeed() && gapToEnter < cfg.charGapMs;
      const scan = isScan ? buf : null;
      reset();
      return { scan, pending: false };
    }
    if (typeof key !== 'string' || key.length !== 1) return { scan: null, pending: buf.length > 0 };

    if (buf.length > 0) {
      const gap = now - lastMs;
      if (gap > cfg.humanResetMs) {
        reset();
      } else if (gap > maxGap) {
        maxGap = gap;
      }
    }
    if (buf.length === 0) firstMs = now;
    buf += key;
    lastMs = now;
    return { scan: null, pending: true };
  }

  /**
   * Called after settleMs of silence. Returns the barcode when the open
   * burst is long enough and machine-speed throughout (a no-suffix scanner),
   * else null. Either way the burst is closed.
   */
  function flush(now) {
    const silence = lastMs ? now - lastMs : Infinity;
    const isScan = buf.length >= cfg.noSuffixMinChars && machineSpeed() && silence >= cfg.settleMs;
    const scan = isScan ? buf : null;
    reset();
    return scan;
  }

  /** Test/introspection helper. */
  function peek() { return { buf, firstMs, lastMs, maxGap }; }

  return { feed, flush, reset, peek, cfg };
}
